import React from 'react';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {
	Checkbox,
	DefaultButton,
	Dialog,
	DialogFooter,
	DialogType,
	PrimaryButton,
	Stack,
	TextField,
} from '@fluentui/react';
import {useNavigate} from 'react-router-dom';
import {
	useUpdateProjectRelevanceByRequirementsMutation,
	useUpdateProjectRelevanceByRegulationsMutation,
} from '../../hooks';

interface ProjectRelevanceFormData {
	isProjectRelevant: boolean;
	comments: string;
}

interface ProjectRelevanceDialogProps {
	isOpen: boolean;
	onClose: () => void;
	modelSeries: string;
	generation: string;
	requirementIds?: string[];
	regulationIds?: string[];
	initialIsProjectRelevant?: boolean;
	initialComments?: string;
}

export const ProjectRelevanceDialog: React.FC<ProjectRelevanceDialogProps> = ({
	isOpen,
	onClose,
	modelSeries,
	generation,
	requirementIds,
	regulationIds,
	initialIsProjectRelevant,
	initialComments,
}) => {
	const {t} = useTranslation('features/vehicleprojects', {
		keyPrefix: 'ProjectRelevantFields',
	});
	const navigate = useNavigate();
	const [updateProjectRelevanceByRequirements] =
		useUpdateProjectRelevanceByRequirementsMutation();
	const [updateProjectRelevanceByRegulations] =
		useUpdateProjectRelevanceByRegulationsMutation();

	const {control, handleSubmit, reset, getValues, watch} =
		useForm<ProjectRelevanceFormData>({
			defaultValues: {
				isProjectRelevant: true,
				comments: '',
			},
		});

	React.useEffect(() => {
		if (isOpen) {
			reset({
				isProjectRelevant: initialIsProjectRelevant ?? true,
				comments: initialComments ?? '',
			});
		}
	}, [isOpen, reset, initialIsProjectRelevant, initialComments]);

	const onSubmit = React.useCallback(
		async (data: ProjectRelevanceFormData) => {
			const comments = data.isProjectRelevant
				? null
				: data.comments.trim() || null;
			if (regulationIds && regulationIds.length > 0) {
				await updateProjectRelevanceByRegulations({
					variables: {
						input: {
							modelSeries,
							generation,
							regulationIds,
							isProjectRelevant: data.isProjectRelevant,
							projectRelevantComments: comments,
						},
					},
				});
			} else if (requirementIds && requirementIds.length > 0) {
				await updateProjectRelevanceByRequirements({
					variables: {
						input: {
							modelSeries,
							generation,
							requirementIds,
							isProjectRelevant: data.isProjectRelevant,
							projectRelevantComments: comments,
						},
					},
				});
			}

			onClose();
			navigate(0);
		},
		[
			modelSeries,
			generation,
			requirementIds,
			regulationIds,
			updateProjectRelevanceByRequirements,
			updateProjectRelevanceByRegulations,
			navigate,
			onClose,
		],
	);

	return (
		<Dialog
			hidden={!isOpen}
			onDismiss={onClose}
			styles={{
				main: {
					minWidth: '400px',
					maxWidth: '90vw',
					width: '600px',
				},
			}}
			dialogContentProps={{
				type: DialogType.normal,
				title: t('DialogTitle'),
			}}
			modalProps={{
				isBlocking: true,
			}}
		>
			<Stack tokens={{childrenGap: 15}}>
				<Controller
					name={'isProjectRelevant'}
					control={control}
					render={({field}) => (
						<Checkbox
							label={t('Relevant')}
							checked={field.value}
							onChange={(_, checked) => field.onChange(checked)}
						/>
					)}
				/>
				<Controller
					name={'comments'}
					control={control}
					rules={{
						validate(value) {
							const isProjectRelevant = getValues('isProjectRelevant');
							if (!isProjectRelevant && !value?.trim()) {
								return t('CommentsRequired');
							}
							return true;
						},
					}}
					render={({field, fieldState}) => {
						const isProjectRelevant = watch('isProjectRelevant');
						return (
							<TextField
								{...field}
								label={t('Justification')}
								multiline
								rows={4}
								errorMessage={fieldState.error?.message}
								placeholder={t('JustificationPlaceholder')}
								required={!isProjectRelevant}
							/>
						);
					}}
				/>
				<div style={{fontSize: '12px', fontStyle: 'italic'}}>
					{t('JustificationAppliedToAll')}
				</div>
			</Stack>
			<DialogFooter>
				<PrimaryButton onClick={handleSubmit(onSubmit)} text={t('Apply')} />
				<DefaultButton onClick={onClose} text={t('Cancel')} />
			</DialogFooter>
		</Dialog>
	);
};
